import {apiService} from '../apiService';

export const schoolManagementService = apiService.injectEndpoints({
    endpoints: (builder) =>
        ({
            getSchool: builder.query({
                query: (schoolId) => ({
                    url: `/school/${schoolId}`
                }),
            }),
            getCurrentSchool: builder.query({
                query: () => ({
                    url: '/school/current'
                }),
            }),
            getSchoolInvitesInfo: builder.query({
                query: (subdomain) => ({
                    url: `/school/${subdomain}/invites-info`
                }),
            }),
            createSchool: builder.mutation({
                query: (body) => ({
                    url: '/school',
                    method: 'POST',
                    data: body,
                })
            }),
            // AD-2697: SIMS Communications Permissions, saved on their own from the MIS Integration tab.
            updateSimsCommunicationsPermissions: builder.mutation({
                query: (body) => ({
                    url: '/school/sims-communications-permissions',
                    method: 'PUT',
                    data: body,
                })
            }),
            updateSchool: builder.mutation({
                query: (body) => ({
                    url: '/school',
                    method: 'PUT',
                    data: body,
                })
            }),
            getContactInvites: builder.query({
                query: (body) => ({
                    url: '/school/contact-invites',
                    method: 'POST',
                    data: body,
                }),
                providesTags: (_) => ['ContactInvites'],
            }),
            resendInvites: builder.mutation({
                query: (contactIds) => ({
                    url: '/school/contact-invites/resend',
                    method: 'POST',
                    data: {contactIds},
                }),
                invalidatesTags: ['ContactInvites'],
            }),
            previewStudentParentInvitations: builder.mutation({
                query: (body) => ({
                    url: '/school/parent-invitations/preview',
                    method: 'POST',
                    data: body,
                })
            }),
            getSchoolUsers: builder.query({
                query: (body) => ({
                    url: '/school/users',
                    method: 'POST',
                    data: body,
                }),
                providesTags: (_) => ['SchoolUsers'],
            }),
            getSyncDetails: builder.query({
                query: (body) => ({
                    url: '/school/sync-details',
                    method: 'POST',
                    data: body,
                }),
                providesTags: (_) => ['SyncDetails'],
            }),
            // AD-2389: a failed Create Applicant is sent to SIMS once more
            retryCreateApplicant: builder.mutation({
                query: (taskId) => ({
                    url: `/school/sync-details/${taskId}/retry`,
                    method: 'POST',
                }),
                invalidatesTags: ['SyncDetails'],
            }),
            executeSync: builder.mutation({
                query: (command) => ({
                    url: '/school/execute-sync',
                    method: 'POST',
                    data: { command },
                }),
                invalidatesTags: ['SyncDetails'],
            }),
            getApplicationStatuses: builder.query({
                query: () => ({
                    url: '/school/application-statuses'
                }),
                keepUnusedDataFor: 300
            }),
            getProfileDescriptions: builder.query({
                query: () => ({
                    url: '/school/profiles/descriptions'
                }),
                providesTags: (_) => ['ProfileDescriptions'],
            }),
            getProfileDescription: builder.query({
                query: (key) => ({
                    url: `/school/profiles/descriptions/${key}`
                })
            }),
            updateProfileDescription: builder.mutation({
                query: (body) => ({
                    url: '/school/profiles/description',
                    method: 'PATCH',
                    data: body,
                }),
                invalidatesTags: ['ProfileDescriptions'],
            }),
            getStudentProfileAccessRestrictionRules: builder.query({
                query: () => ({
                    url: '/school/student-profile-access-restrictions'
                }),
                providesTags: () => ['StudentProfileAccessRestrictions'],
            }),
            getStudentProfileAccessRestrictionRule: builder.query({
                query: (ruleId) => ({
                    url: `/school/student-profile-access-restrictions/${ruleId}`
                }),
                providesTags: (_result, _error, ruleId) => [
                    {type: 'StudentProfileAccessRestrictions', id: ruleId}
                ],
            }),
            upsertStudentProfileAccessRestrictionRule: builder.mutation({
                query: (body) => ({
                    url: '/school/student-profile-access-restrictions',
                    method: 'PUT',
                    data: body,
                }),
                invalidatesTags: ['StudentProfileAccessRestrictions'],
            }),
            deleteStudentProfileAccessRestrictionRule: builder.mutation({
                query: (ruleId) => ({
                    url: `/school/student-profile-access-restrictions/${ruleId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['StudentProfileAccessRestrictions'],
            }),
        }),
});

export const {
    useGetSchoolQuery,
    useUpdateSimsCommunicationsPermissionsMutation,
    useGetCurrentSchoolQuery,
    useGetSchoolInvitesInfoQuery,
    useCreateSchoolMutation,
    useUpdateSchoolMutation,
    useGetContactInvitesQuery,
    useResendInvitesMutation,
    usePreviewStudentParentInvitationsMutation,
    useGetSchoolUsersQuery,
    useGetSyncDetailsQuery,
    useExecuteSyncMutation,
    useRetryCreateApplicantMutation,
    useGetApplicationStatusesQuery,
    useGetProfileDescriptionsQuery,
    useGetProfileDescriptionQuery,
    useUpdateProfileDescriptionMutation,
    useGetStudentProfileAccessRestrictionRulesQuery,
    useGetStudentProfileAccessRestrictionRuleQuery,
    useLazyGetStudentProfileAccessRestrictionRuleQuery,
    useUpsertStudentProfileAccessRestrictionRuleMutation,
    useDeleteStudentProfileAccessRestrictionRuleMutation,
} = schoolManagementService;
